import { CheckBoxWithLabelWidget,DropdownButtonType, List, ListItem } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';

import { incrementRouterSpaceReactKey } from '~src/spacesPlatform/actions/IncrementRouterSpaceReactKeyAction';

import { Messages } from '../../codegen/Localize';
import { FormikSingleSelectDropdown } from '../../commonInfra/components/FormikSingleSelectDropdown';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { IServerUserInfo } from '../../userManagement/users/models/IServerUserInfo';
import { UserSettingsNotification,UserSettingsNotificationParams, UserSettingsNotificationType } from '../notifications/UserSettingsNotification';
import { ITimeZone } from '../serverApi/types/ITimeZone';
import { UserSettings } from '../services/resources/UserSettings';
import { IGetTimeZonesResult,TimeZones } from '../services/TimeZones';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';
import _ = require('underscore');

import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { HelpService } from '../../commonInfra/services/HelpService';
import {
  getSessionManagementServiceInstance,
  SessionManagementService
} from '../../sessionManagement/SessionManagementService';

export interface TimeZoneFormFields {
  timeZoneId: string;
}

export type TimeZoneFormProps = {
  initialValues: TimeZoneFormFields;
  serverUser: IServerUserInfo;
}

const userIsViewingTheirOwnSettings = (sessionManagementService: SessionManagementService, serverUser: IServerUserInfo) => {
  return sessionManagementService.sessionUsername() === serverUser.username
    && sessionManagementService.sessionUserDomainName() === serverUser.domainName;
};

const notifyTimeZoneChange = (result: IResult, username: string) => {
  const userSettingsNotificationParams: UserSettingsNotificationParams = {
    result: result,
    username: username,
    userSettingsNotificationType: UserSettingsNotificationType.ChangeTimeZone
  };
  UserSettingsNotification.notify(userSettingsNotificationParams);
};

function showTimeZoneButton(formik: FormikProps<TimeZoneFormFields>, initialTimeZoneId: string): boolean {
  if (!formik.dirty || !formik.isValid || formik.isSubmitting || (formik.values.timeZoneId.trim() === initialTimeZoneId)) {
    return false;
  }
  return true;
}

export function TimeZoneForm(props: TimeZoneFormProps): JSX.Element {
  const [curatedItems, setCuratedItems] = React.useState([]);
  const [usersCurrentTimeZone, setUsersCurrentTimeZone] = React.useState('');
  const [allItems, setAllItems] = React.useState([]);
  const [showFullList, setShowFullList] = React.useState(false);

  React.useEffect(() => {
    async function fetchTimeZones() {
      let timeZonesResult: IGetTimeZonesResult = await TimeZones.getAll();
      let curatedItems: ListItem[] = timeZonesResult.curatedTimeZonesList.map((timeZone: ITimeZone) => ({
        content: timeZone.displayName,
        value: timeZone.timeZoneId,
        testId: timeZone.timeZoneId,
      }));
      let allItems: ListItem[] = timeZonesResult.fullTimeZonesList.map((timeZone: ITimeZone) => ({
        content: timeZone.displayName,
        value: timeZone.timeZoneId,
        testId: timeZone.timeZoneId,
      }));

      const initialTimeZoneId = props.initialValues.timeZoneId;
      const serverTimeZone: ITimeZone = timeZonesResult.serverTimeZone;
      let currentTimeZoneId = initialTimeZoneId;
      let showFullList = false;
      let nullOrInvalidTimeZone = false;
      if (initialTimeZoneId) {
        const tzInCuratedList = _.any(timeZonesResult.curatedTimeZonesList, tz => tz.timeZoneId === initialTimeZoneId);
        const tzInExtraList = _.any(timeZonesResult.extraTimeZonesList, tz => tz.timeZoneId === initialTimeZoneId);
        if (!tzInCuratedList && !tzInExtraList) {
          currentTimeZoneId = serverTimeZone.timeZoneId;
          nullOrInvalidTimeZone = true;
        } else if (tzInExtraList) {
          showFullList = true;
        }
      } else {
        currentTimeZoneId = serverTimeZone.timeZoneId;
        nullOrInvalidTimeZone = true;
      }

      const serverTimeZoneInCuratedList = _.any(timeZonesResult.curatedTimeZonesList,
        tz => tz.timeZoneId === serverTimeZone.timeZoneId);
      if (nullOrInvalidTimeZone && !serverTimeZoneInCuratedList) {
        showFullList = true;
      }
      setCuratedItems(curatedItems);
      setAllItems(allItems);
      setShowFullList(showFullList);
      setUsersCurrentTimeZone(currentTimeZoneId);
    }

    if (curatedItems.length === 0 && allItems.length === 0) {
      fetchTimeZones();
    }
  });

  const handleSubmit = React.useCallback(
    async (timeZoneId: string, formik: FormikHelpers<TimeZoneFormFields>) => {
      if (timeZoneId !== usersCurrentTimeZone) {
        const result: IResult = await UserSettings.updateUserLocalTimeZoneData(0, timeZoneId, true, props.serverUser.id);
        const sessionManagementService: SessionManagementService = getSessionManagementServiceInstance();
        if (userIsViewingTheirOwnSettings(sessionManagementService, props.serverUser)) {
          await sessionManagementService.refreshSessionInfo();
          getAppStore().dispatch(incrementRouterSpaceReactKey());
        }
        if (result) {
          notifyTimeZoneChange(result, props.serverUser.username);
          setUsersCurrentTimeZone(timeZoneId);
        } else {
          formik.resetForm();
        }
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props, setUsersCurrentTimeZone]
  );

  const handleFullListCheckboxChange = React.useCallback((checked: boolean) => {
    setShowFullList(checked);
  }, [setShowFullList]);

  return (
    <Formik
      initialValues={{ timeZoneId: usersCurrentTimeZone }}
      onSubmit={function(values: TimeZoneFormFields, formik: FormikHelpers<TimeZoneFormFields>) {
        handleSubmit(values.timeZoneId, formik);
      }}
      enableReinitialize={true}
    >
      {(formik: FormikProps<TimeZoneFormFields>) => (
          <form onSubmit={formik.handleSubmit}>
            <table>
              <tbody className={UserSettingsStyle.tbody}>
                {/* Time Zone Section */}
                <tr>
                  <td className={UserSettingsStyle.labelText}>
                    <h4>{Messages.userSettings_TimeZoneSetting_Header()}</h4>
                  </td>
                  <td className={UserSettingsStyle.detailsValue}>
                    {Messages.timeZoneHint()}
                    <a className="tb-lr-padded" rel="noopener noreferrer" target="_blank" href={HelpService.getServerHelpUrlByCategory('subscription-timezone')}>
                      <span>{Messages.timeZoneSettingLearnMore()}</span>
                    </a>
                  </td>
                </tr>
                <tr data-tb-test-id="time-zone-section">
                  <td></td>
                  <td className={UserSettingsStyle.detailsValueSmallPadding}>
                      <FormikSingleSelectDropdown
                          name="timeZoneId"
                          list={<List items={showFullList ? allItems : curatedItems} className={UserSettingsStyle.dropdownList}/>}
                          type={DropdownButtonType.Outline}
                          label={''}
                          className={UserSettingsStyle.dropdown}
                          testId={'timeZone'}/>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td className={UserSettingsStyle.detailsValue}>
                    <CheckBoxWithLabelWidget
                      label={Messages.editTimeZoneShowFullTimeZoneListEnable()}
                      checked={showFullList}
                      testId={'show-full-time-zone-list'}
                      handleChange={handleFullListCheckboxChange}
                      containerStyle={{marginLeft: '0px'}}/>
                  </td>
                </tr>
                {(showTimeZoneButton(formik, usersCurrentTimeZone) &&
                  <InlineCancelAndSaveButtons
                  testId={'edit-time-zone'}
                  formik={formik}
                  />
                )}
              </tbody>
            </table>
            <div>
            </div>
          </form>
      )}
    </Formik>
  );
}
